import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { 
  DollarSign, 
  Save,
  RefreshCw,
  Plus,
  Eye,
  Check,
  AlertTriangle,
  Globe,
  Receipt,
  Info,
  Undo2,
  Calendar
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

import { API_BASE_URL } from '../utils/api';
const API_URL = API_BASE_URL;

export default function AdminPricing() {
  const { admin } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [activeTab, setActiveTab] = useState('current');
  
  // Billing state
  const [pricingVersions, setPricingVersions] = useState([]);
  const [activePricing, setActivePricing] = useState(null);
  const [taxRates, setTaxRates] = useState([]);
  
  // Get default effective date (1st of next month)
  const getDefaultEffectiveDate = () => {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return nextMonth.toISOString().split('T')[0];
  };
  
  const [newPricing, setNewPricing] = useState({
    prices: {
      free: { name: 'Free', retail_price_monthly: 0, wholesale_price_monthly: 0, description: 'For individuals trying out the platform' },
      pro: { name: 'Pro', retail_price_monthly: 20, wholesale_price_monthly: 15, description: 'For teams that need the full suite of features' }
    },
    effective_date: getDefaultEffectiveDate(),
    notes: ''
  });
  const [pricingImpact, setPricingImpact] = useState(null);
  const [savingPricing, setSavingPricing] = useState(false);
  const [reverting, setReverting] = useState(false);

  useEffect(() => {
    fetchPricing();
    fetchBillingData();
  }, []);

  const fetchPricing = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin-portal/pricing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPricing(response.data.plans);
    } catch (error) {
      toast.error('Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch pricing versions
      const versionsRes = await axios.get(`${API_URL}/api/billing/pricing/versions`, { headers });
      setPricingVersions(versionsRes.data.versions || []);
      
      // Fetch active pricing
      const activeRes = await axios.get(`${API_URL}/api/billing/pricing/active`);
      setActivePricing(activeRes.data);
      
      // Update newPricing with current active values
      if (activeRes.data?.prices) {
        setNewPricing(prev => ({
          ...prev,
          prices: {
            free: { ...prev.prices.free, ...activeRes.data.prices.free },
            pro: { ...prev.prices.pro, ...activeRes.data.prices.pro }
          }
        }));
      }
      
      // Fetch tax rates
      const taxRes = await axios.get(`${API_URL}/api/billing/tax-rates`, { headers });
      setTaxRates(taxRes.data.tax_rates || []);
    } catch (error) {
      console.error('Failed to load billing data:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin-portal/pricing`, pricing, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pricing updated successfully');
    } catch (error) {
      toast.error('Failed to save pricing');
    } finally {
      setSaving(false);
    }
  };

  const updatePlan = (plan, field, value) => {
    setPricing(prev => ({
      ...prev,
      [plan]: {
        ...prev[plan],
        [field]: field.includes('price') ? parseFloat(value) || 0 : value
      }
    }));
  };

  const createPricingVersion = async () => {
    setSavingPricing(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      // Convert date string to ISO datetime
      const effectiveDateTime = new Date(newPricing.effective_date + 'T00:00:00Z').toISOString();
      
      await axios.post(`${API_URL}/api/billing/pricing/versions`, {
        ...newPricing,
        effective_date: effectiveDateTime
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pricing version created');
      fetchBillingData();
      
      // Reset to defaults for next entry
      setNewPricing(prev => ({
        ...prev,
        effective_date: getDefaultEffectiveDate(),
        notes: ''
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create pricing version');
    } finally {
      setSavingPricing(false);
    }
  };

  const fetchPricingImpact = async (versionId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/billing/pricing/impact/${versionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPricingImpact({ ...response.data, versionId });
    } catch (error) {
      toast.error('Failed to fetch impact preview');
    }
  };

  const activatePricingVersion = async (versionId) => {
    if (!window.confirm('Are you sure you want to activate this pricing version? New signups will get the new price from the effective date. Existing customers will see the change at their next billing cycle.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API_URL}/api/billing/pricing/versions/${versionId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pricing version activated');
      fetchBillingData();
      setPricingImpact(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate pricing version');
    }
  };

  const revertPricingVersion = async (versionId) => {
    if (!window.confirm('Are you sure you want to revert to the previous pricing version?')) {
      return;
    }
    
    setReverting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API_URL}/api/billing/pricing/versions/${versionId}/revert`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revert pricing version');
    } finally {
      setReverting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Pricing & Billing</h1>
          <p className="text-slate-400 mt-1">
            Configure subscription prices and manage billing settings
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50 border-slate-700 mb-6">
          <TabsTrigger value="current" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <DollarSign className="w-4 h-4 mr-2" />
            Public Pricing
          </TabsTrigger>
          <TabsTrigger value="versions" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <Receipt className="w-4 h-4 mr-2" />
            Price Changes
          </TabsTrigger>
          <TabsTrigger value="tax" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <Globe className="w-4 h-4 mr-2" />
            Tax Rates
          </TabsTrigger>
        </TabsList>

        {/* Current Pricing Tab */}
        <TabsContent value="current" className="space-y-6">
          {/* Info Banner */}
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 mt-0.5" />
                <div className="text-sm text-blue-300">
                  <strong>Wholesale Model:</strong> Partners pay wholesale price and bill their customers directly at their own rates.
                  Direct customers (no partner) pay retail price.
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing Cards */}
          <div className="grid gap-6 md:grid-cols-2">
            {pricing && ['free', 'pro'].map(plan => (
              <Card 
                key={plan} 
                className={`bg-slate-800/50 border-slate-700 ${
                  plan === 'pro' ? 'ring-2 ring-amber-500/50' : ''
                }`}
              >
                <CardHeader>
                  <CardTitle className="text-white capitalize flex items-center justify-between">
                    {pricing[plan]?.name || plan}
                    {plan === 'pro' && (
                      <span className="text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-400">Most Popular</span>
                    )}
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    {pricing[plan]?.description || 'No description'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Retail Price (Direct Customers)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        type="number"
                        step="0.01"
                        className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                        value={pricing[plan]?.retail_price_monthly || pricing[plan]?.price_monthly || 0}
                        onChange={(e) => updatePlan(plan, 'retail_price_monthly', e.target.value)}
                        disabled={plan === 'free'}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">/user/mo</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-cyan-300">Wholesale Price (Partners)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-500" />
                      <Input
                        type="number"
                        step="0.01"
                        className="pl-10 bg-slate-900/50 border-cyan-600/50 text-white"
                        value={pricing[plan]?.wholesale_price_monthly || 0}
                        onChange={(e) => updatePlan(plan, 'wholesale_price_monthly', e.target.value)}
                        disabled={plan === 'free'}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">/user/mo</span>
                    </div>
                  </div>

                  {plan !== 'free' && (
                    <div className="p-3 rounded bg-green-500/10 border border-green-500/30">
                      <div className="text-sm text-green-400">
                        Partner Margin: {formatCurrency((pricing[plan]?.retail_price_monthly || 20) - (pricing[plan]?.wholesale_price_monthly || 15))}/user
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label className="text-slate-300">Description</Label>
                    <Input
                      className="bg-slate-900/50 border-slate-600 text-white"
                      value={pricing[plan]?.description || ''}
                      onChange={(e) => updatePlan(plan, 'description', e.target.value)}
                      placeholder="Plan description"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} className="bg-red-500 hover:bg-red-600">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Public Pricing
            </Button>
          </div>
        </TabsContent>

        {/* Price Changes Tab */}
        <TabsContent value="versions" className="space-y-6">
          {/* Create New Version */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-blue-400" />
                Schedule Price Change
              </CardTitle>
              <CardDescription className="text-slate-400">
                Set the effective date for new customers. Existing customers' prices change at their next renewal after this date.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Pro Plan Pricing */}
              <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-700">
                <h4 className="font-medium text-white mb-4">New Pro Plan Pricing</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Retail Price (Direct Customers)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-slate-400">$</span>
                      <Input
                        type="number"
                        value={newPricing.prices.pro.retail_price_monthly}
                        onChange={(e) => setNewPricing(prev => ({
                          ...prev,
                          prices: {
                            ...prev.prices,
                            pro: { ...prev.prices.pro, retail_price_monthly: parseFloat(e.target.value) || 0 }
                          }
                        }))}
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                      <span className="text-slate-400">/user/mo</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-cyan-300">Wholesale Price (Partners)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-cyan-400">$</span>
                      <Input
                        type="number"
                        value={newPricing.prices.pro.wholesale_price_monthly}
                        onChange={(e) => setNewPricing(prev => ({
                          ...prev,
                          prices: {
                            ...prev.prices,
                            pro: { ...prev.prices.pro, wholesale_price_monthly: parseFloat(e.target.value) || 0 }
                          }
                        }))}
                        className="bg-slate-900 border-cyan-600/50 text-white"
                      />
                      <span className="text-slate-400">/user/mo</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Effective Date */}
              <div>
                <Label className="text-slate-300 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Effective Date for New Customers
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="date"
                    value={newPricing.effective_date}
                    onChange={(e) => setNewPricing(prev => ({
                      ...prev,
                      effective_date: e.target.value
                    }))}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-48 bg-slate-900 border-slate-600 text-white"
                  />
                  <span className="text-slate-400 text-sm">
                    Existing customers' prices will change at their next renewal after this date
                  </span>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-slate-300">Release Notes (optional)</Label>
                <Textarea
                  value={newPricing.notes}
                  onChange={(e) => setNewPricing(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Describe the reason for this price change..."
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                />
              </div>

              <Button 
                onClick={createPricingVersion} 
                disabled={savingPricing}
                className="bg-blue-500 hover:bg-blue-600"
              >
                {savingPricing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Scheduled Price Change
              </Button>
            </CardContent>
          </Card>

          {/* Active Pricing */}
          <Card className="bg-green-500/10 border-green-500/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-400" />
                  Active: {activePricing?.version || 'v0 (default)'}
                </CardTitle>
                {activePricing?.previous_version_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => revertPricingVersion(activePricing.id)}
                    disabled={reverting}
                    className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  >
                    {reverting ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Undo2 className="w-4 h-4 mr-1" />}
                    Revert to Previous
                  </Button>
                )}
              </div>
              {activePricing?.effective_date && (
                <CardDescription className="text-slate-400">
                  Effective from: {formatDate(activePricing.effective_date)}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {activePricing?.prices && Object.entries(activePricing.prices).map(([plan, info]) => (
                  <div key={plan} className="p-3 rounded bg-slate-900/50">
                    <h4 className="font-medium text-white capitalize mb-2">{plan} Plan</h4>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Retail:</span>
                        <span className="text-white">{formatCurrency(info.retail_price_monthly)}/user</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Wholesale:</span>
                        <span className="text-cyan-400">{formatCurrency(info.wholesale_price_monthly)}/user</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Pricing Versions List */}
          {pricingVersions.length > 0 && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Version History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pricingVersions.map((version) => (
                    <div 
                      key={version.id} 
                      className={`p-4 rounded-lg border ${
                        version.status === 'active' 
                          ? 'bg-green-500/10 border-green-500/30' 
                          : version.status === 'scheduled' 
                            ? 'bg-blue-500/10 border-blue-500/30'
                            : version.status === 'reverted'
                              ? 'bg-amber-500/10 border-amber-500/30'
                              : 'bg-slate-900/50 border-slate-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white">{version.version}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              version.status === 'active' 
                                ? 'bg-green-500/20 text-green-400' 
                                : version.status === 'scheduled'
                                  ? 'bg-blue-500/20 text-blue-400'
                                  : version.status === 'reverted'
                                    ? 'bg-amber-500/20 text-amber-400'
                                    : 'bg-slate-700 text-slate-400'
                            }`}>
                              {version.status}
                            </span>
                            {version.effective_date && (
                              <span className="text-xs text-slate-400">
                                Effective: {formatDate(version.effective_date)}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-slate-400 mt-1">
                            Pro: {formatCurrency(version.prices?.pro?.retail_price_monthly || 0)} retail / 
                            {formatCurrency(version.prices?.pro?.wholesale_price_monthly || 0)} wholesale
                          </div>
                          {version.notes && (
                            <p className="text-xs text-slate-500 mt-1">{version.notes}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {version.status === 'scheduled' && (
                            <>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => fetchPricingImpact(version.id)}
                                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Preview
                              </Button>
                              <Button 
                                size="sm"
                                onClick={() => activatePricingVersion(version.id)}
                                className="bg-green-500 hover:bg-green-600"
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Activate Now
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Impact Preview */}
          {pricingImpact && (
            <Card className="bg-amber-500/10 border-amber-500/50">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  Price Change Impact Preview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 rounded bg-slate-900/50">
                    <div className="text-2xl font-bold text-white">{pricingImpact.affected_direct_customers}</div>
                    <div className="text-sm text-slate-400">Direct customers</div>
                  </div>
                  <div className="p-3 rounded bg-slate-900/50">
                    <div className="text-2xl font-bold text-white">{pricingImpact.affected_partners}</div>
                    <div className="text-sm text-slate-400">Partners</div>
                  </div>
                  <div className="p-3 rounded bg-slate-900/50">
                    <div className="text-2xl font-bold text-white">{pricingImpact.affected_partner_managed_customers}</div>
                    <div className="text-sm text-slate-400">Partner customers</div>
                  </div>
                </div>
                
                <div className="p-4 rounded bg-slate-900/50">
                  <h4 className="font-medium text-white mb-2">Price Changes</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-400">Retail: </span>
                      <span className="text-white">{formatCurrency(pricingImpact.price_changes?.retail?.current)}</span>
                      <span className="text-slate-400"> → </span>
                      <span className={pricingImpact.price_changes?.retail?.change > 0 ? 'text-red-400' : 'text-green-400'}>
                        {formatCurrency(pricingImpact.price_changes?.retail?.new)}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-400">Wholesale: </span>
                      <span className="text-white">{formatCurrency(pricingImpact.price_changes?.wholesale?.current)}</span>
                      <span className="text-slate-400"> → </span>
                      <span className={pricingImpact.price_changes?.wholesale?.change > 0 ? 'text-red-400' : 'text-green-400'}>
                        {formatCurrency(pricingImpact.price_changes?.wholesale?.new)}
                      </span>
                    </div>
                  </div>
                </div>
                
                {pricingImpact.effective_date && (
                  <div className="text-sm text-slate-400">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Effective date: <span className="text-blue-400 font-medium">{formatDate(pricingImpact.effective_date)}</span>
                  </div>
                )}
                
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setPricingImpact(null)}
                  className="border-slate-600 text-slate-300"
                >
                  Close Preview
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tax Rates Tab */}
        <TabsContent value="tax" className="space-y-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-400" />
                Tax Rates
              </CardTitle>
              <CardDescription className="text-slate-400">
                Configure tax rates for different regions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {taxRates.map((tax) => (
                  <div key={tax.country_code} className="flex items-center justify-between p-4 rounded bg-slate-900/50 border border-slate-700">
                    <div>
                      <span className="font-medium text-white">{tax.country}</span>
                      <span className="text-slate-400 ml-2">({tax.name})</span>
                      {tax.is_default && (
                        <span className="ml-2 text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">Default</span>
                      )}
                    </div>
                    <span className="text-xl font-bold text-white">{(tax.rate * 100).toFixed(0)}%</span>
                  </div>
                ))}
                {taxRates.length === 0 && (
                  <p className="text-slate-400 text-center py-8">No tax rates configured</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="py-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                  <Info className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-medium text-white mb-1">Tax Configuration</h3>
                  <p className="text-sm text-slate-400">
                    Tax rates are applied to invoices based on the customer's location.
                    New Zealand GST (15%) is the default. Additional tax rates can be added as needed for international expansion.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
